import * as v from "valibot";

/**
 * Brief / lead schema — shared by the frontend form (app/ui/Hire/) and the
 * Hono server (server/src/index.ts). Both halves validate with the same
 * Valibot schema so the server re-checks everything the client checked
 * (a hostile client can post arbitrary JSON, so the server is the source
 * of truth). The Cloudflare Worker version at workers/briefs/ is preserved
 * as an alternative deploy path and uses the same schema.
 */

export const SERVICE_OPTIONS = [
  { value: "games", label: "Jogos" },
  { value: "engines", label: "Motores (Unity / Unreal)" },
  { value: "native-android", label: "Android nativo" },
  { value: "react-native", label: "React Native" },
  { value: "ar-vr", label: "Realidade aumentada / virtual" },
  { value: "ai", label: "Inteligência artificial" },
  { value: "fintech", label: "Fintech / automação" },
  { value: "other", label: "Outra coisa" },
] as const;

export type ServiceValue = (typeof SERVICE_OPTIONS)[number]["value"];

export const BUDGET_OPTIONS = [
  { value: "under-5k", label: "Até US$ 5 mil" },
  { value: "5-15k", label: "US$ 5 mil – 15 mil" },
  { value: "15-50k", label: "US$ 15 mil – 50 mil" },
  { value: "50-150k", label: "US$ 50 mil – 150 mil" },
  { value: "discuss", label: "Prefiro conversar" },
] as const;

export type BudgetValue = (typeof BUDGET_OPTIONS)[number]["value"];

export const TIMELINE_OPTIONS = [
  { value: "asap", label: "Para ontem (este mês)" },
  { value: "1-3mo", label: "1 a 3 meses" },
  { value: "3-6mo", label: "3 a 6 meses" },
  { value: "flexible", label: "Sem pressa" },
] as const;

export type TimelineValue = (typeof TIMELINE_OPTIONS)[number]["value"];

const serviceValues = SERVICE_OPTIONS.map((o) => o.value) as [
  ServiceValue,
  ...ServiceValue[],
];
const budgetValues = BUDGET_OPTIONS.map((o) => o.value) as [
  BudgetValue,
  ...BudgetValue[],
];
const timelineValues = TIMELINE_OPTIONS.map((o) => o.value) as [
  TimelineValue,
  ...TimelineValue[],
];

export const BriefSchema = v.object({
  name: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(2, "Escreva seu nome, com ao menos 2 letras."),
    v.maxLength(80, "Esse nome está longo demais. Pode encurtar?"),
  ),
  email: v.pipe(
    v.string(),
    v.trim(),
    v.email("Esse e-mail não parece válido."),
    v.maxLength(120, "Esse e-mail é longo demais."),
  ),
  service: v.picklist(serviceValues, "Escolha uma área."),
  budget: v.picklist(budgetValues, "Escolha uma faixa de orçamento."),
  timeline: v.picklist(timelineValues, "Escolha um prazo."),
  message: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(10, "Escreva ao menos 10 caracteres contando o que você precisa."),
    v.maxLength(2000, "Resuma em até 2000 caracteres."),
  ),
  /** Optional cubie id the visitor came from, for analytics. */
  sourceCubie: v.optional(v.string()),
  /** Anti-bot: real users don't fill this hidden input. */
  honeypot: v.optional(v.pipe(v.string(), v.maxLength(0))),
});

export type Brief = v.InferOutput<typeof BriefSchema>;

export const BriefDraftSchema = v.partial(BriefSchema);
export type BriefDraft = v.InferOutput<typeof BriefDraftSchema>;
